"use client"

import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// USDA freezer guidance — sources:
//   • USDA Food Safety and Inspection Service: "Freezing and Food Safety"
//     https://www.fsis.usda.gov/food-safety/safe-food-handling-and-preparation/food-safety-basics/freezing-and-food-safety
//   • USDA recommends freezer at 0°F for indefinite safety; quality windows
//     vary by food. See storage table below.

const SECTIONS = [
  { id: "what", t: "What freezer burn is" },
  { id: "rules", t: "USDA prevention rules" },
  { id: "windows", t: "Storage windows" },
  { id: "package", t: "How to package" },
  { id: "rescue", t: "Rescue burnt food" },
] as const

export function FreezerBurnPreventionPageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Freezer Burn Prevention Guide"
      heroEyebrow="Food storage"
      heroTitlePre="How to stop"
      heroTitleItalic="freezer burn"
      heroTitlePost="for good."
      heroSubtitle="Freezer burn is dehydration plus oxidation — caused by air in contact with frozen food. Here's how USDA recommends you stop it: 0°F, airtight packaging, and the right storage window per food type."
      heroMeta={["⏱ ~5 min read", "USDA freezer guidance"]}
      heroShare={<ToolShareBar title="How to stop freezer burn for good" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            The USDA rule
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Freezer at <em className="italic text-[#ff9951]">0°F</em>, packaging{" "}
            <em className="italic text-[#ff9951]">airtight</em>, food still safe{" "}
            <em className="italic text-[#ff9951]">indefinitely</em>.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Target temp
              </div>
              <div className="text-[22px] font-black tracking-tight">0°F</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Air contact
              </div>
              <div className="text-[22px] font-black tracking-tight">None</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Is freezer-burnt food safe to eat?",
          answer:
            "Yes — per USDA, food held at 0°F is safe indefinitely. Freezer burn affects quality (texture, flavor) but not safety. Trim affected areas before cooking.",
        },
        {
          question: "What causes freezer burn?",
          answer:
            "Sublimation: water inside frozen food turns directly from ice to vapor on the surface, leaving dehydrated patches. Air contact accelerates it, and so do temperature swings (auto-defrost cycles, frequent door openings).",
        },
        {
          question: "Does vacuum sealing actually work?",
          answer:
            "Yes — it's the most effective consumer method. By removing the air around the food, you eliminate the sublimation pathway. Vacuum-sealed meat can hold 2–3× the typical USDA quality window.",
        },
        {
          question: "Why does ice cream get burnt so fast?",
          answer:
            "Two reasons: high air content (foam) means lots of internal surface area, and most home freezers cycle warmer than 0°F. Press plastic wrap directly onto the surface before resealing the carton.",
        },
        {
          question: "Are auto-defrost freezers worse for freezer burn?",
          answer:
            "Yes. The defrost heater cycles the freezer up to ~32°F briefly, which causes tiny thaw-refreeze cycles inside the food. Manual-defrost freezers maintain steadier temps and preserve quality longer — see our auto-vs-manual cost calculator.",
        },
      ]}
      relatedTools={[
        {
          title: "Auto vs Manual Defrost",
          category: "Energy & Cost",
          description: "Lifetime cost comparison.",
          href: "/tools/auto-vs-manual-defrost-cost-calculator",
          time: "~30 sec",
        },
        {
          title: "Freezer Meat Storage",
          category: "Food Storage",
          description: "How long meat stays good.",
          href: "/tools/freezer-meat-storage-calculator",
          time: "~30 sec",
        },
        {
          title: "Vacuum Sealer Calculator",
          category: "Food Storage",
          description: "Right model for your usage.",
          href: "/tools/freezer-vacuum-sealer-calculator",
          time: "~45 sec",
        },
        {
          title: "Temperature Settings Guide",
          category: "Temperature",
          description: "USDA 40°F / 0°F set points.",
          href: "/tools/refrigerator-temperature-settings-guide",
          time: "~5 min",
        },
      ]}
      cta={{
        title: "Time for a real freezer?",
        body: "If you batch-cook or buy meat in bulk, a dedicated upright or chest freezer holds steadier temperatures than your kitchen unit.",
        buttonLabel: "Browse all freezers",
        buttonHref: "/best-freezers",
      }}
    >
      {/* ============ ARTICLE BODY WITH STICKY TOC ============ */}
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          {/* Sticky TOC */}
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          {/* Article body */}
          <article>
            <GuideTool
              eyebrow="Interactive checker"
              title="Check your freezer-burn risk"
              intro="Three questions about how the food went in \u2014 the verdict tells you how exposed it is and the one change that helps most."
              questions={[
                            {
                              id: "pack",
                              label: "How is the food packaged?",
                              options: [
                                { value: "vacuum", label: "Vacuum sealed" },
                                { value: "bag", label: "Freezer bag, air pressed out" },
                                { value: "store", label: "Original store packaging" }
                              ],
                            },
                            {
                              id: "time",
                              label: "How long will it stay frozen?",
                              options: [
                                { value: "short", label: "Under a month" },
                                { value: "mid", label: "1\u20136 months" },
                                { value: "long", label: "Over 6 months" }
                              ],
                            },
                            {
                              id: "unit",
                              label: "What kind of freezer?",
                              options: [
                                { value: "chest", label: "Chest or manual-defrost upright" },
                                { value: "auto", label: "Auto-defrost, door opens often" }
                              ],
                            }
              ]}
              compute={(a) => {
                            if (a.pack === "vacuum" && a.time !== "long") {
                              return { tone: "good", verdict: "Low risk", detail: "Vacuum sealing removes the air that freezer burn needs, and a short stay gives sublimation no time to work. This combination keeps quality essentially intact." }
                            }
                            if (a.pack === "store" && a.time !== "short") {
                              return { tone: "bad", verdict: "High risk \u2014 repackage now", detail: "Store trays and overwrap are air-permeable; past a month they are the classic freezer-burn setup. Move the food to a vacuum bag or a freezer bag with the air pressed out, and it will hold months longer." }
                            }
                            const drift = a.unit === "auto" ? " Auto-defrost cycles add small temperature swings that speed surface drying \u2014 keep burn-prone items away from the door and vents." : ""
                            return { tone: "warn", verdict: "Moderate risk \u2014 one upgrade helps", detail: "Pressed freezer bags handle mid-length storage, but for anything past a few months a vacuum seal (or a double wrap for store packs) is the difference that shows at the table." + drift }
                          }}
              ctaLabel="Check my risk"
              footnote="Mechanism and time bands per the guide below (USDA quality guidance)."
            />
            {/* What freezer burn is */}
            <section id="what" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                What freezer burn actually is
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Freezer burn is dehydration plus oxidation. Water inside frozen food sublimates —
                turns directly from ice to vapor — and escapes wherever there&apos;s air contact. The
                surface dries out, oxidizes, and turns grayish-brown.
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Per USDA, freezer-burnt food is{" "}
                <em className="font-semibold not-italic text-[#ff9951]">safe to eat</em> — frozen
                food held at 0°F is safe indefinitely. The damage is to quality: texture, flavor,
                and color. Trim the affected layer before cooking and the rest is fine.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    Two ways it happens faster
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    <strong>Air contact</strong> (loose packaging, ripped bag) and{" "}
                    <strong>temperature swings</strong> (auto-defrost cycles, frequent door
                    openings) both accelerate sublimation. Stop both and the burn stops.
                  </p>
                </div>
              </div>
            </section>

            {/* USDA prevention rules */}
            <section id="rules" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                USDA&apos;s prevention rules
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  {
                    n: "01",
                    t: "Freeze at 0°F or below",
                    d: "Verify with an appliance thermometer — most factory dials are imprecise. The colder and steadier, the slower the sublimation.",
                  },
                  {
                    n: "02",
                    t: "Remove all air from packaging",
                    d: "Air contact is the dominant cause. Vacuum-seal where possible; otherwise press wrap directly onto the food, then bag.",
                  },
                  {
                    n: "03",
                    t: "Use freezer-grade materials",
                    d: "Heavy-duty freezer bags, freezer paper, or freezer-safe containers — not regular plastic wrap or sandwich bags. Material thickness matters.",
                  },
                  {
                    n: "04",
                    t: "Freeze quickly, in flat shapes",
                    d: "The faster food freezes, the smaller the ice crystals (less cell damage). Flat packages also stack and thaw more evenly.",
                  },
                  {
                    n: "05",
                    t: "Label and rotate",
                    d: "Date every package. Use older items first. USDA's quality windows assume best-by ordering — don't bury 8-month-old chicken under last week's groceries.",
                  },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Storage windows */}
            <section id="windows" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                USDA quality windows
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                These are <strong>quality windows</strong>, not safety windows. Food stays safe
                indefinitely at 0°F — but quality degrades over the times below. Source: USDA
                Food Safety and Inspection Service.
              </p>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Food
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Quality window at 0°F
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Note
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Raw beef, pork, lamb (whole cuts)", "4–12 months", "Steaks, roasts, chops"],
                      ["Ground meat", "3–4 months", "Hamburger, ground turkey"],
                      ["Whole poultry", "1 year", "Chicken, turkey"],
                      ["Poultry pieces", "9 months", "Breasts, thighs"],
                      ["Lean fish (cod, sole)", "6 months", "—"],
                      ["Fatty fish (salmon, mackerel)", "2–3 months", "Oils oxidize faster"],
                      ["Cooked meat & casseroles", "2–3 months", "—"],
                      ["Soups & stews", "2–3 months", "—"],
                      ["Bread & baked goods", "2–3 months", "Wrap tightly, slice first"],
                      ["Vegetables (blanched)", "8–12 months", "Blanching preserves color/texture"],
                      ["Fruits", "8–12 months", "Berries, sliced fruit"],
                      ["Hot dogs & lunch meat (sealed)", "1–2 months", "—"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4">
                          <span className="font-mono font-bold text-[#ff9951]">{r[1]}</span>
                        </td>
                        <td className="px-5 py-4 text-gray-600">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[13px] text-gray-500 mt-4">
                Source: USDA Food Safety and Inspection Service, &ldquo;Freezing and Food
                Safety.&rdquo; Vacuum-sealing typically extends these windows 2–3×.
              </p>
            </section>

            {/* Package */}
            <section id="package" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Packaging that works
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Vacuum sealer (best — removes all air)",
                  "Heavy-duty freezer bags + press out air",
                  "Freezer paper, double-wrapped",
                  "Freezer-safe containers with tight lids",
                  "Plastic wrap pressed directly onto surface, then bagged",
                  "Aluminum foil (heavy-duty), wrapped tight",
                  "Don't use: regular sandwich bags",
                  "Don't use: thin cling wrap alone",
                  "Don't use: styrofoam meat trays alone",
                  "Don't use: original retail packaging long-term",
                ].map((t, i) => (
                  <div
                    key={t}
                    className="flex items-start gap-3 p-4 rounded-lg"
                    style={{ background: i < 6 ? "rgba(16,166,92,0.06)" : "rgba(220,38,38,0.05)" }}
                  >
                    <div
                      className="w-6 h-6 rounded-full text-white flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: i < 6 ? "#10a65c" : "#dc2626" }}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Rescue */}
            <section id="rescue" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Rescuing freezer-burnt food
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Three rescue strategies, in order of preference.
              </p>
              <div className="space-y-3">
                {[
                  {
                    t: "Trim the burnt surface",
                    d: "Cut off the dehydrated, discolored layer. The food underneath is fine — texture is roughly equivalent to fresh.",
                  },
                  {
                    t: "Cook in liquid",
                    d: "Stews, braises, and soups mask textural damage. The slow cooking re-hydrates the surface and the broth carries flavor.",
                  },
                  {
                    t: "Marinate before grilling",
                    d: "An acidic marinade (citrus, vinegar) for 1–2 hours pulls moisture back into the surface. Works for steak and chicken.",
                  },
                ].map((r, i) => (
                  <div
                    key={r.t}
                    className="grid grid-cols-[36px_1fr] sm:grid-cols-[44px_1fr] gap-4 sm:gap-5 p-4 sm:p-5 rounded-lg"
                    style={{ background: i === 0 ? "rgba(255,153,81,0.04)" : "#f9fafb" }}
                  >
                    <div
                      className="font-mono font-bold"
                      style={{
                        fontSize: 14,
                        letterSpacing: "0.05em",
                        color: i === 0 ? "#ff9951" : "#9ca3af",
                        lineHeight: 1.4,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="text-[18px] font-extrabold tracking-[-0.015em] mb-1 text-gray-900">
                        {r.t}
                      </h3>
                      <p className="text-[14px] text-gray-600 leading-[1.55]">{r.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </article>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Steadier temperatures"
          title="Upright freezers that prevent burn"
          description="Manual-defrost models hold 0°F more consistently than auto-defrost units."
          collectionType="upright-freezer"
          fallbackCollectionType="chest-freezer"
        />
      </section>

      {/* ============ SECONDARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Chest freezers for long-term storage"
          description="Best thermal stability and the largest USDA quality windows."
          collectionType="chest-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
